import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import {
  AddressDto,
  DeliveryDayDto,
  DeliveryMethodDto,
  DeliverySlotDto,
  OrderDto,
  OrderSubmitAddressesDto,
  OrderSubmitDto,
  PaymentMethodDto,
  PaymentStartDto,
} from '@/api';
import { deliverySlotsApi, ordersApi, StoreResponse, StoreResponses } from '@/stores/api';
import ErrorKey from '@/util/ErrorKey';
import { Addresses } from '@/util/Addresses';
import { Step, Steps } from '@/util/Step';
import { ShopContext } from '@/util/ShopContext';
import moment from 'moment';

@Module({
  namespaced: true,
  name: 'OrderStore',
})
export default class OrderStore extends VuexModule {
  // Note: This store is **partially** cached using vuex-persist. See vuex-persist-config.ts!
  // Note: After an order is submitted, resetState() is called to reset local storage
  private submissionPending = false;
  private step: Step = Step.ADDRESS;
  private deliveryAddress: AddressDto | null = null;
  private billingAddress: AddressDto | null = null;
  private deliverySlot: DeliverySlotDto | null = null;
  private paymentMethod: PaymentMethodDto | null = null;
  private availableDeliverySlots: DeliveryDayDto[] | null = null;
  private deliveryMethod: DeliveryMethodDto = DeliveryMethodDto.PICKUP;
  private guestCartId?: string = undefined;
  private confirmationMailAddress: string | null = null;
  private birthday: string | null = null;
  private currentOrderId = '';
  private driverNotice = '';
  private loadingDeliverySlots = true;
  private languageCode = '';

  private frontendRedirectUrlTemplate =
    `${window.location.protocol}//${window.location.hostname}:${window.location.port}` +
    `/order/%%state%%?orderId=%%orderId%%`;

  get loadingSlots(): boolean {
    return this.loadingDeliverySlots;
  }

  @Action
  public async fetchOrderById(orderId: string): Promise<StoreResponse<OrderDto>> {
    try {
      const response = await ordersApi.getOrderByIdAndShopCode(ShopContext.shopCode, orderId);
      return StoreResponses.apiSuccess(response);
    } catch (e) {
      return StoreResponses.error(e, e.errorKey);
    }
  }

  get orderSubmitDto(): OrderSubmitDto | undefined {
    if (this.currentDeliverySlot && this.currentPaymentMethod && this.orderAddresses) {
      const email = this.confirmationMailAddress ? this.confirmationMailAddress : undefined;
      const birthday = this.birthday ? this.birthday : undefined;
      return {
        shoppingCartId: this.guestCartId, addresses: this.orderAddresses,
        deliverySlot: this.currentDeliverySlot, requestedPaymentMethod: this.currentPaymentMethod,
        email, birthday, frontendRedirectUrl: this.frontendRedirectUrlTemplate, driverNotice: this.currentDriverNotice,
        languageCode: this.languageCode,
      };
    }
    return undefined;
  }

  get orderAddresses(): OrderSubmitAddressesDto | undefined {
    if (this.currentBillingAddress && this.currentDeliveryAddress) {
      return {
        billingAddressId: this.currentBillingAddress.id, billingAddressToCreate: this.currentBillingAddress,
        deliveryAddressId: this.currentDeliveryAddress.id, deliveryAddressToCreate: this.currentDeliveryAddress,
      };
    }
    return undefined;
  }

  @Action
  public async fetchAvailableDeliverySlots(): Promise<StoreResponse<DeliveryDayDto[]>> {
    this.context.commit('setLoadingDeliverySlots', true);

    let location;
    if (this.deliveryMethod === DeliveryMethodDto.PICKUP) {
      location = {countryCode: 'AT', postCode: '6511'};
    } else if (this.deliveryAddress) {
      location = {countryCode: this.deliveryAddress.countryCode, postCode: this.deliveryAddress.postCode};
    } else {
      this.context.commit('setLoadingDeliverySlots', false);
      return StoreResponses.suppressedError([], ErrorKey.MissingOrderAddressException);
    }

    try {
      const deliveryResponse = await deliverySlotsApi.getAvailableDeliverySlots(
        ShopContext.shopCode, this.deliveryMethod, location.countryCode, location.postCode,
      );
      this.context.commit('setAvailableDeliverySlots', deliveryResponse.data);
      return StoreResponses.success(deliveryResponse.data);
    } catch (e) {
      this.context.commit('setAvailableDeliverySlots', []);

      if (e.errorKey && e.errorKey === ErrorKey.DeliveryNotSupportedException) {
        return StoreResponses.suppressedError(e, ErrorKey.DeliveryNotSupportedException);
      }

      return StoreResponses.error(e, ErrorKey.UnknownBackendError);
    } finally {
      this.context.commit('setLoadingDeliverySlots', false);
    }
  }


  get currentStep(): Step {
    return this.step;
  }

  get isSubmissionPending(): boolean {
    return this.submissionPending;
  }

  get isGuestOrder(): boolean {
    return !!this.guestCartId;
  }

  get currentDeliveryAddress(): AddressDto | null {
    return this.deliveryAddress;
  }

  get currentBillingAddress(): AddressDto | null {
    return this.billingAddress;
  }

  @Action
  public async startPayment(): Promise<StoreResponse<PaymentStartDto>> {
    try {
      if (this.currentOrderId) {
        const response = await ordersApi.startPayment(ShopContext.shopCode, this.currentOrderId);
        return StoreResponses.success(response.data);
      }
    } catch (e) {
      return StoreResponses.error(e, ErrorKey.UnknownBackendError);
    }
    return StoreResponses.error('', ErrorKey.EmptyOrderException);
  }

  get currentAvailableDeliverySlots(): DeliveryDayDto[] | null {
    return this.availableDeliverySlots;
  }

  get currentDeliveryMethod(): DeliveryMethodDto | null {
    return this.deliveryMethod;
  }

  get currentPaymentMethod(): PaymentMethodDto | null {
    return this.paymentMethod;
  }

  get currentDeliverySlot(): DeliverySlotDto | null {
    return this.deliverySlot;
  }

  get currentConfirmationMailAddress(): string | null {
    return this.confirmationMailAddress;
  }

  get currentBirthday(): string | null {
    return this.birthday;
  }

  get orderId(): string {
    return this.currentOrderId;
  }

  get currentDriverNotice() {
    return this.driverNotice;
  }


  @Action
  public async preSubmitOrder(): Promise<StoreResponse<string>> {
    if (!this.orderSubmitDto) {
      return StoreResponses.error(null, ErrorKey.EmptyOrderException);
    } else if (this.submissionPending) {
      return StoreResponses.error(null, ErrorKey.AlreadyPending);
    }
    try {
      this.context.commit('setSubmissionPending', true);
      const response = await ordersApi.preSubmitOrder(ShopContext.shopCode, this.orderSubmitDto);
      const orderId = response.data;
      if (orderId) {
        this.updateOrderId(orderId);
      }
      return StoreResponses.success(response.data);
    } catch (e) {
      return StoreResponses.error(e, ErrorKey.UnknownBackendError);
    } finally {
      this.context.commit('resetState');
      this.context.commit('setSubmissionPending', false);
    }
  }

  @Action
  public setAddressesIfValid(payload: { billingAddress: AddressDto, deliveryAddress: AddressDto }) {
    const billingValid = Addresses.checkAddress(payload.billingAddress);
    const deliveryValid = Addresses.checkAddress(payload.deliveryAddress);
    this.context.commit('setBillingAddress', billingValid ? payload.billingAddress : null);
    this.context.commit('setDeliveryAddress', deliveryValid ? payload.deliveryAddress : null);
  }

  @Action
  public updateDeliveryMethod(deliveryMethod: DeliveryMethodDto) {
    this.context.commit('setDeliveryMethod', deliveryMethod);
    this.context.commit('setDeliverySlot', null);
  }

  @Action
  public updateDeliverySlot(deliverySlot: DeliverySlotDto) {
    this.context.commit('setDeliverySlot', deliverySlot);
  }

  @Action
  public updatePaymentMethod(paymentMethod: PaymentMethodDto) {
    this.context.commit('setPaymentMethod', paymentMethod);
  }

  @Action
  public resetDeliverySlot() {
    this.context.commit('setDeliverySlot', null);
  }

  @Mutation
  public setLanguageCode(languageCode: string) {
    this.languageCode = languageCode;
  }

  @Mutation
  public resetState() {
    this.paymentMethod = null;
    this.billingAddress = null;
    this.deliveryAddress = null;
    this.deliverySlot = null;
    this.deliveryMethod = DeliveryMethodDto.PICKUP;
    this.driverNotice = '';
    this.confirmationMailAddress = null;
    this.birthday = null;
    this.guestCartId = undefined;
  }

  @Action
  public updateOrderId(orderId: string) {
    this.context.commit('setOrderId', orderId);
  }

  @Action
  public proceedToStep(nextStep: Step): boolean {
    if (nextStep > Steps.getLastStep()) {
      return false;
    }
    for (let stepIndex = 1; stepIndex < nextStep; stepIndex++) {
      if (!Steps.isStepFinished(stepIndex, this)) {
        return false;
      }
    }
    this.context.commit('setCurrentStep', nextStep);
    return true;
  }

  @Action
  public resetStep() {
    this.context.commit('setCurrentStep', Step.ADDRESS);
  }

  @Action
  public setBirthdayValidated(birthday: string): StoreResponse<void> {
    const age = moment().diff(moment(birthday).startOf('day'), 'years');
    if (age < 18 || age > 200) {
      this.context.commit('setBirthday', null);
      return StoreResponses.forErrorKey(ErrorKey.AgeRestrictionException);
    } else {
      this.context.commit('setBirthday', birthday);
      return StoreResponses.success(undefined);
    }
  }

  @Mutation
  private setDeliveryMethod(deliveryMethod: DeliveryMethodDto) {
    this.deliveryMethod = deliveryMethod;
  }

  @Mutation
  private setDeliverySlot(deliverySlot: DeliverySlotDto | null) {
    this.deliverySlot = deliverySlot;
  }

  @Mutation
  public setDeliveryAddress(address: AddressDto | null) {
    this.deliveryAddress = address;
  }

  @Mutation
  public setDriverNotice(driverNotice: string) {
    this.driverNotice = driverNotice;
  }

  @Mutation
  public setBillingAddress(address: AddressDto | null) {
    this.billingAddress = address;
  }

  @Mutation
  private setAvailableDeliverySlots(payload: DeliveryDayDto[]) {
    this.availableDeliverySlots = payload;
  }

  @Mutation
  private setPaymentMethod(payload: PaymentMethodDto | null) {
    this.paymentMethod = payload;
  }

  @Mutation
  public setConfirmationMailAddress(payload: string) {
    this.confirmationMailAddress = payload;
  }

  @Mutation
  public setGuestCartId(id: string) {
    this.guestCartId = id;
  }

  @Mutation
  private setBirthday(birthday: string | null) {
    this.birthday = birthday;
  }

  @Mutation
  private setOrderId(orderId: string) {
    this.currentOrderId = orderId;
  }

  @Mutation
  private setCurrentStep(step: Step) {
    this.step = step;
  }

  @Mutation
  private setSubmissionPending(newValue: boolean) {
    this.submissionPending = newValue;
  }

  @Mutation
  private setLoadingDeliverySlots(newValue: boolean) {
    this.loadingDeliverySlots = newValue;
  }
}
